<template>
  <div>
    <div class="activity-stage">
      <div :class="['shifty-targets', { tablet: isMobile }]">
        <ul class="text-steps">
          <li
            v-for="step in currentRound.steps"
            :key="step.answer"
            :class="`list-step step-${step.stepIndex}`"
          >
            <color-block-text
              :text="step.answer"
              :text-step="step.stepIndex"
              :hint-class="getUppercaseHint"
            ></color-block-text>
          </li>
        </ul>
        <slot name="keyboard"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { TimelineLite } from 'gsap';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  components: {
    ColorBlockText
  },

  mixins: [TypingActivityHelper, ActivityManager, TouchAndHoldHelper, ErrorLogger],

  data() {
    return {
      activityName: 'ShiftyTargets'
    };
  },

  computed: {
    isMobile() {
      return this.device.isMobile;
    },
    getUppercaseHint() {
      return this.expectedKey &&
        this.expectedKey !== ' ' &&
        this.expectedKey === this.expectedKey.toUpperCase()
        ? 'hint'
        : '';
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
    },

    getKeyboardConfig() {
      const characters = ' ';

      return {
        keys: characters
      };
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type each letter. Use the Shift Key for capitals.',
          voice: `${this.voiceInstructionsPrefix}/shiftytargets-${this.getRandomVoice()}-1.mp3`,
          delay: 2
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.shouldConfigureKeyboard();
      this.enableKeyboard();
      this.errorLoggerStart();
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
    },

    shouldConfigureKeyboard() {
      this.configureKeyboard({
        ...this.$store.state.keyboard.settings,
        keys: this.getKeyboardConfig().keys + this.currentStep.answer,
        hideNumberRow: true
      });
    },

    showHandComponent() {
      const timeline = new TimelineLite();

      if (this.getUppercaseHint) {
        timeline
          .set('.phoenix-target-hand', { css: { display: 'block' } })
          .set('.active.highlight', { css: { className: '-=highlight' } })
          .set('.hold-key', { css: { className: '-=hide-target' } });
      } else {
        timeline
          .set('.phoenix-target-hand', { css: { display: 'none' } })
          .set('.active', { css: { className: '+=hide-target' } });
      }
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();
      this.$nextTick(() => this.showHandComponent());
      if (this.isEndOfRound) {
        this.sndBell();
        this.disableKeyboard();
        this.hideKeyboard();
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline
          .delay(1)
          .to('.shifty-targets', 0.5, { opacity: 0 })
          .call(this.completeActivity);
      } else {
        this.finishStep();
        timeline
          .to('.text-steps', 0.5, { opacity: 0 })
          .delay(0.5)
          .call(this.setupNewRound)
          .call(this.shouldConfigureKeyboard)
          .call(this.showKeyboard)
          .call(this.enableKeyboard)
          .call(this.showHandComponent)
          .to('.text-steps', 0.5, { opacity: 1 });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.shifty-targets {
  min-height: 430px;
  display: flex;

  ul {
    list-style-type: none;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    width: 100%;
    p {
      font-size: 42px;
      letter-spacing: 20px;
    }
  }

  &.tablet {
    min-height: 300px;
  }
}
</style>
